import { useCallback, useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";
import { api } from "../lib/api";
import { getConsent, setConsent, CONSENT_EVENT } from "../lib/consent";
import { initPostHog, initMetaPixel, pixelPageView } from "../lib/trackers";

// Single source of truth for cookie consent on the public site. Shows a banner on
// first visit, loads PostHog (analytics) / Meta Pixel (marketing) only after the
// matching consent, and exposes a "Cookie Settings" reopen via window event.
export const openCookieSettings = () =>
  window.dispatchEvent(new CustomEvent("ss-open-cookie-settings"));

export default function CookieConsent() {
  const { pathname } = useLocation();
  const [banner, setBanner] = useState(false);
  const [panel, setPanel] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: false, marketing: false });
  const [pixelId, setPixelId] = useState("");

  // Apply whatever consent is currently stored (load trackers accordingly).
  const applyConsent = useCallback((c) => {
    if (!c) return;
    if (c.analytics) initPostHog();
    if (c.marketing && pixelId) initMetaPixel(pixelId);
  }, [pixelId]);

  // Fetch the Meta Pixel ID (if any) so we know whether marketing tracking exists.
  useEffect(() => {
    let cancelled = false;
    api.get("/site-config").then(({ data }) => {
      if (!cancelled) setPixelId((data?.meta_pixel_id || "").trim());
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Initial state: show banner if no decision yet; otherwise honour stored choice.
  useEffect(() => {
    const c = getConsent();
    if (!c) {
      setBanner(true);
    } else {
      setPrefs({ analytics: !!c.analytics, marketing: !!c.marketing });
      applyConsent(c);
    }
  }, [applyConsent]);

  // Re-apply once the pixel ID arrives (marketing consent may already be granted).
  useEffect(() => {
    const c = getConsent();
    if (c) applyConsent(c);
  }, [pixelId, applyConsent]);

  // React to changes from the settings panel.
  useEffect(() => {
    const onChange = (e) => applyConsent(e.detail);
    const onOpen = () => {
      const c = getConsent();
      setPrefs({ analytics: !!(c && c.analytics), marketing: !!(c && c.marketing) });
      setPanel(true);
    };
    window.addEventListener(CONSENT_EVENT, onChange);
    window.addEventListener("ss-open-cookie-settings", onOpen);
    return () => {
      window.removeEventListener(CONSENT_EVENT, onChange);
      window.removeEventListener("ss-open-cookie-settings", onOpen);
    };
  }, [applyConsent]);

  // Fire a Meta Pixel PageView on client-side route changes (only once loaded).
  useEffect(() => { pixelPageView(); }, [pathname]);

  const save = (choice) => {
    const c = setConsent(choice);
    setPrefs({ analytics: c.analytics, marketing: c.marketing });
    setBanner(false);
    setPanel(false);
  };

  const acceptAll = () => save({ analytics: true, marketing: true });
  const rejectAll = () => save({ analytics: false, marketing: false });
  const savePrefs = () => save(prefs);

  if (!banner && !panel) return null;

  return (
    <>
      {/* First-visit banner */}
      {banner && !panel && (
        <div
          role="dialog"
          aria-label="Cookie consent"
          aria-live="polite"
          className="fixed inset-x-0 bottom-0 z-[60] p-4 md:p-6"
          data-testid="cookie-banner"
        >
          <div className="mx-auto max-w-[1100px] rounded-3xl bg-[#1A1A1A] text-[#F7F5F0] shadow-2xl ring-1 ring-white/10 p-6 md:p-8">
            <div className="flex flex-col lg:flex-row lg:items-center gap-6">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <Cookie className="h-5 w-5 text-[#C8102E]" />
                  <h2 className="font-display text-xl uppercase tracking-tight">Cookies on SugaSpice</h2>
                </div>
                <p className="text-sm text-[#C9C5BE] leading-relaxed max-w-2xl">
                  We use essential cookies to make this site work. With your consent we'd also
                  like to use analytics and marketing cookies to understand how the site is used
                  and to measure our advertising. You can accept all, reject non-essential, or
                  choose what's on. See our{" "}
                  <Link to="/cookies" className="underline hover:text-white" onClick={() => setBanner(false)}>Cookie Policy</Link>{" "}
                  and{" "}
                  <Link to="/privacy" className="underline hover:text-white" onClick={() => setBanner(false)}>Privacy Policy</Link>.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row lg:flex-col gap-2 shrink-0">
                <button onClick={acceptAll} data-testid="cookie-accept-all" className="btn-pill bg-[#C8102E] text-[#F7F5F0] hover:bg-[#A50D24] justify-center">
                  Accept all
                </button>
                <button onClick={rejectAll} data-testid="cookie-reject-all" className="btn-pill border border-white/25 text-[#F7F5F0] hover:bg-white/10 justify-center">
                  Reject non-essential
                </button>
                <button onClick={() => setPanel(true)} data-testid="cookie-manage" className="text-sm underline text-[#C9C5BE] hover:text-white py-2">
                  Manage preferences
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preferences panel */}
      {panel && (
        <div className="fixed inset-0 z-[70] flex items-end md:items-center justify-center p-4" data-testid="cookie-settings-panel">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => { setPanel(false); if (!getConsent()) setBanner(true); }} />
          <div className="relative w-full max-w-lg rounded-3xl bg-[#F7F5F0] text-[#1A1A1A] shadow-2xl ring-1 ring-black/10 p-7 md:p-9 max-h-[85vh] overflow-y-auto">
            <div className="flex items-start justify-between gap-4 mb-5">
              <div>
                <h2 className="font-display text-2xl uppercase tracking-tight">Cookie preferences</h2>
                <p className="text-sm text-[#5C5C5C] mt-1">Choose which cookies we can use. Essential cookies are always on.</p>
              </div>
              <button onClick={() => { setPanel(false); if (!getConsent()) setBanner(true); }} aria-label="Close cookie preferences" className="p-1 rounded-full hover:bg-black/5">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-3">
              <Row title="Strictly necessary" desc="Required for the site to work (e.g. remembering your cookie choice). Always active." on disabled />
              <Row title="Analytics" desc="Helps us understand how visitors use the site so we can improve it (PostHog). Session recording is switched off." on={prefs.analytics} onToggle={() => setPrefs((p) => ({ ...p, analytics: !p.analytics }))} testId="toggle-analytics" />
              <Row title="Marketing" desc="Lets us measure and improve our advertising (Meta/Facebook Pixel)." on={prefs.marketing} onToggle={() => setPrefs((p) => ({ ...p, marketing: !p.marketing }))} testId="toggle-marketing" />
            </div>

            <div className="mt-7 flex flex-col sm:flex-row gap-2">
              <button onClick={savePrefs} data-testid="cookie-save-prefs" className="btn-pill bg-[#1A1A1A] text-[#F7F5F0] hover:bg-[#333] justify-center flex-1">Save preferences</button>
              <button onClick={acceptAll} data-testid="cookie-panel-accept-all" className="btn-pill bg-[#C8102E] text-[#F7F5F0] hover:bg-[#A50D24] justify-center flex-1">Accept all</button>
              <button onClick={rejectAll} data-testid="cookie-panel-reject-all" className="btn-pill border border-[#1A1A1A]/20 text-[#1A1A1A] hover:bg-black/5 justify-center flex-1">Reject non-essential</button>
            </div>
            <p className="mt-4 text-xs text-[#7a7a7a]">
              Read more in our <Link to="/cookies" className="underline" onClick={() => setPanel(false)}>Cookie Policy</Link>.
            </p>
          </div>
        </div>
      )}
    </>
  );
}

function Row({ title, desc, on, disabled, onToggle, testId }) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-2xl border border-[#D1C9BE]/60 bg-white p-4">
      <div>
        <p className="font-semibold text-sm">{title}</p>
        <p className="text-xs text-[#5C5C5C] mt-1 leading-relaxed">{desc}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={!!on}
        aria-label={title}
        disabled={disabled}
        onClick={onToggle}
        data-testid={testId}
        className={`mt-0.5 relative h-6 w-11 shrink-0 rounded-full transition-colors ${on ? "bg-[#C8102E]" : "bg-[#C9C5BE]"} ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
      >
        <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${on ? "left-[22px]" : "left-0.5"}`} />
      </button>
    </div>
  );
}
